import { mapOverviewFishGroupSummaryToDomain } from '@/domain/fishGroups/mappers';
import type {
    CageBioscopeHistoryItemDto,
    CageContextResponseDto,
    CageListItemDto,
    CageStatus,
    CreateCagesDto,
    CreateCagesResponse,
    FishType,
    GetCageTypesResponse,
    GetWeightDistributionsResponse,
    UpdateCageDto,
    UpdateCageResponse,
} from '@/services/types';

import type { ApiWire } from '../shared/types';

import type {
    BioscopeBlock,
    CageBlock,
    ClientBlock,
    DataAvailabilityBlock,
    DomainCageContext,
    LicenseBlock,
    LocationBlock,
    StockBlock,
} from './context.types';
import type {
    CageBioscopeHistoryItem,
    CageListItem,
    CageType,
    CreateCagesRequest,
    CreatedCagesResult,
    CurrentBioscopeInfo,
    DomainCageStatus,
    DomainFishType,
    HistoricalBioscopeInfo,
    LastDataInfo,
    UpdateCageRequest,
    UpdateCageResult,
} from './types';
import type { WeightDistributionData } from './weightDistribution.types';

/** A pen's bioscope placements, each keyed by bioscope, day and its count among repeats. */
export function mapCageBioscopeHistoryToDomain(
    history: ApiWire<CageBioscopeHistoryItemDto>[]
): CageBioscopeHistoryItem[] {
    const seen = new Map<string, number>();
    return history.map((item) => {
        const base = `${item.bioscopeId}-${item.from}`;
        const repeat = seen.get(base) ?? 0;
        seen.set(base, repeat + 1);
        return {
            key: repeat === 0 ? base : `${base}-${repeat}`,
            bioscopeId: item.bioscopeId,
            from: item.from,
            to: item.to ?? null,
        };
    });
}

export function mapCageListItemToDomain(response: ApiWire<CageListItemDto>): CageListItem {
    return {
        id: response.id,
        name: response.name,
        status: response.status,
        historicalAt: response.historicalAt ?? null,
        cageType: response.cageType ?? null,
        fishType: response.fishType ?? null,
        externalId: response.externalId ?? null,
        externalName: response.externalName ?? null,
        clientId: response.clientId ?? null,
        clientName: response.clientName ?? null,
        locationId: response.locationId ?? null,
        locationName: response.locationName ?? null,
        currentBioscopeId: response.currentBioscopeId ?? null,
        bioscopeHistory: mapCageBioscopeHistoryToDomain(response.bioscopeHistory),
        firstMeasurement: response.firstMeasurement ?? null,
        lastMeasurement: response.lastMeasurement ?? null,
        activeFishGroup: response.activeFishGroup
            ? mapOverviewFishGroupSummaryToDomain(response.activeFishGroup)
            : null,
    };
}

export function mapCageTypeResponseToDomain(response: GetCageTypesResponse): CageType[] {
    return response.cageTypes.map((cageType) => ({
        id: cageType.id,
        name: cageType.name,
        isDefault: cageType.isDefault,
    }));
}

export function mapCreateCagesRequestToApi(request: CreateCagesRequest): CreateCagesDto {
    return {
        locationId: request.locationId,
        cageTypeId: request.cageTypeId,
        fishTypeId: request.fishTypeId,
        names: request.names,
        externalName: request.externalName || undefined,
        externalId: request.externalId || undefined,
    };
}

export function mapCreateCagesResponseToDomain(response: CreateCagesResponse): CreatedCagesResult {
    return {
        ids: response.ids,
    };
}

export function mapUpdateCageRequestToApi(request: UpdateCageRequest): UpdateCageDto {
    return {
        name: request.name,
        cageTypeId: request.cageTypeId,
        fishTypeId: request.fishTypeId,
        externalName: request.externalName || undefined,
        externalId: request.externalId || undefined,
    };
}

export function mapUpdateCageResponseToDomain(response: UpdateCageResponse): UpdateCageResult {
    return {
        id: response.id,
    };
}

export function mapWeightDistributionsResponseToDomain(
    response: GetWeightDistributionsResponse
): WeightDistributionData[] {
    return response.weightDistributions.map((weightDistribution) => ({
        data: weightDistribution.data.map((distributionData) => ({
            cv: distributionData.cv,
            dailyDistributionTypeTag: distributionData.dailyDistributionTypeTag,
            kurtosis: distributionData.kurtosis,
            skewness: distributionData.skewness,
            weight: distributionData.weight,
        })),
        day: weightDistribution.day,
        dayPartTag: weightDistribution.daypartTag,
        histograms: weightDistribution.histograms.map((histogram) => ({
            bucket: {
                from: histogram.bucket.from,
                to: histogram.bucket.to,
            },
            cnt: histogram.cnt,
            cntPct: histogram.cntPct,
            dailyDistributionTypeTag: histogram.dailyDistributionTypeTag,
        })),
        weightUnitTag: weightDistribution.weightUnitTag,
    }));
}

// ── Cage context ────────────────────────────────────────────────────────────────
//
// One field-by-field pass, block by block. Explicit rather than `return response` so a backend
// rename lands here instead of in fifty components — the mapper is the firewall that keeps
// `@/services` types out of the UI.

export function mapCageContextToDomain(response: CageContextResponseDto): DomainCageContext {
    return {
        cage: mapCageBlock(response.cage),
        location: mapLocationBlock(response.location),
        client: mapClientBlock(response.client),
        stock: mapStockBlock(response.stock),
        dataAvailability: mapDataAvailabilityBlock(response.dataAvailability),
        bioscope: mapBioscopeBlock(response.bioscope),
        licenses: response.licenses.map(mapLicenseBlock),
    };
}

function mapCageBlock(cage: CageContextResponseDto['cage']): CageBlock {
    return {
        id: cage.id,
        name: cage.name,
        externalId: cage.externalId,
        externalName: cage.externalName,
        cageType: cage.cageType
            ? {
                  id: cage.cageType.id,
                  name: cage.cageType.name,
                  isDefault: cage.cageType.isDefault,
              }
            : undefined,
    };
}

function mapLocationBlock(location: CageContextResponseDto['location']): LocationBlock {
    return {
        id: location.id,
        name: location.name,
        timezone: location.timezone,
        locationType: {
            id: location.locationType.id,
            name: location.locationType.name,
            shortName: location.locationType.shortName,
        },
    };
}

function mapClientBlock(client: CageContextResponseDto['client']): ClientBlock {
    return {
        id: client.id,
        name: client.name,
        featureFlags: client.featureFlags,
    };
}

function mapStockBlock(stock: CageContextResponseDto['stock']): StockBlock {
    return {
        fishType: stock.fishType
            ? {
                  id: stock.fishType.id,
                  name: stock.fishType.name,
                  shortName: stock.fishType.shortName,
              }
            : undefined,
        liceType: {
            id: stock.liceType.id,
            name: stock.liceType.name,
            shortName: stock.liceType.shortName,
            isDefault: stock.liceType.isDefault,
        },
        sWeightPct: stock.sWeightPct,
    };
}

function mapDataAvailabilityBlock(
    dataAvailability: CageContextResponseDto['dataAvailability']
): DataAvailabilityBlock {
    return {
        firstMeasurementDay: dataAvailability.firstMeasurementDay,
        hasColorWelfare: dataAvailability.hasColorWelfare,
        historicalSince: dataAvailability.historicalSince,
    };
}

function mapBioscopeBlock(bioscope: CageContextResponseDto['bioscope']): BioscopeBlock {
    return {
        id: bioscope.id,
        version: bioscope.version,
        winchExists: bioscope.winchExists,
        winchId: bioscope.winchId,
    };
}

// `from`/`to` stay the `YYYY-MM-DD` strings the API sends. They used to be parsed into `Date`s
// that nothing ever read.
function mapLicenseBlock(license: CageContextResponseDto['licenses'][0]): LicenseBlock {
    return {
        code: license.code,
        from: license.from,
        to: license.to,
    };
}

// Mappers for CageStatus
export function mapCageStatusToDomain(response: CageStatus): DomainCageStatus {
    return {
        currentBioscope: mapCurrentBioscopeToDomain(response.currentBioscope),
        historicalBioscopes: response.historicalBioscopes.map(mapHistoricalBioscopeToDomain),
        lastData: mapLastDataToDomain(response.lastData),
    };
}

function mapCurrentBioscopeToDomain(
    currentBioscope: CageStatus['currentBioscope']
): CurrentBioscopeInfo {
    return {
        id: currentBioscope.id,
        lastAlive: currentBioscope.lastAlive,
    };
}

function mapHistoricalBioscopeToDomain(
    historicalBioscope: CageStatus['historicalBioscopes'][0]
): HistoricalBioscopeInfo {
    return {
        bioscopeId: historicalBioscope.bioscopeId,
        from: historicalBioscope.from,
        to: historicalBioscope.to ?? null,
    };
}

function mapLastDataToDomain(lastData: CageStatus['lastData']): LastDataInfo {
    return {
        depthLast: lastData?.depthLast,
        lastAlive: lastData?.lastAlive,
        lastMoveFromDate: lastData?.lastMoveFromDate,
        pitchLast: lastData?.pitchLast,
        rollLast: lastData?.rollLast,
        timestampLast: lastData?.timestampLast,
    };
}

// Mappers for FishTypes
function mapFishTypeToDomain(fishType: FishType): DomainFishType {
    return {
        id: fishType.id,
        name: fishType.name,
        shortName: fishType.shortName,
        isDefault: fishType.is_default,
    };
}

export function mapFishTypesToDomain(response: FishType[]): DomainFishType[] {
    return response.map(mapFishTypeToDomain);
}
